/**
 * Cookie Consent Banner
 *
 * Non-blocking bottom banner for cookie consent.
 * Appears on first visit or when policy version changes.
 */

import { useCookieConsent } from '../context/CookieConsentContext';

const CookieConsentBanner = () => {
  const {
    showBanner,
    acceptAll,
    rejectNonEssential,
    openPreferences
  } = useCookieConsent();

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 animate-slide-up">
      {/* Backdrop blur bar */}
      <div className="bg-white/95 dark:bg-[#1c1c1e]/95 backdrop-blur-xl border-t border-[#d2d2d7] dark:border-white/10 shadow-2xl">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-4 sm:py-5">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            {/* Cookie Icon + Text */}
            <div className="flex items-start gap-3 flex-1">
              <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-[#f5f5f7] to-[#e8e8ed] dark:from-[#2c2c2e] dark:to-[#3a3a3c] flex items-center justify-center">
                <svg className="w-5 h-5 text-[#1d1d1f] dark:text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div>
                <h3 className="text-[15px] font-semibold text-[#1d1d1f] dark:text-white mb-1">
                  We use cookies
                </h3>
                <p className="text-[13px] text-[#6e6e73] dark:text-[#98989d] leading-relaxed">
                  We use cookies to improve your experience and analyze how you use QuickMeet.
                  You can choose which cookies to accept.{' '}
                  <a
                    href="/privacy"
                    className="text-[#06c] hover:underline"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Learn more
                  </a>
                </p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 sm:gap-3 w-full sm:w-auto">
              {/* Manage Preferences Link */}
              <button
                onClick={openPreferences}
                className="order-3 sm:order-1 text-[13px] font-medium text-[#06c] hover:text-[#0055d4] transition-colors py-2 sm:py-0"
              >
                Manage Preferences
              </button>

              {/* Reject Non-Essential */}
              <button
                onClick={rejectNonEssential}
                className="order-2 px-4 py-2.5 bg-[#f5f5f7] dark:bg-[#2c2c2e] text-[#1d1d1f] dark:text-white text-[13px] font-medium rounded-xl hover:bg-[#e8e8ed] dark:hover:bg-[#3a3a3c] transition-all"
              >
                Reject Non-Essential
              </button>

              {/* Accept All */}
              <button
                onClick={acceptAll}
                className="order-1 sm:order-3 px-5 py-2.5 bg-gradient-to-r from-[#06c] to-[#0055d4] text-white text-[13px] font-semibold rounded-xl hover:shadow-lg hover:shadow-[#06c]/25 transition-all"
              >
                Accept All
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CookieConsentBanner;
